import React, { useEffect, useState } from 'react';
import {
    Modal,
    Button,
    Box,
    Tooltip,
    IconButton,
    TextField
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { ItemCategory } from '../../interfaces/item-category.interface';
import { ItemCategoryService } from '../../services/item-category.service';
import {AuthService} from "../../services/auth.service";
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const ItemCategoryList: React.FC = () => {
    const [itemCategories, setItemCategories] = useState<ItemCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);

    const [selectedItemCategory, setSelectedItemCategory] = useState<ItemCategory | null>(null);
    const [formData, setFormData] = useState({
        itemCategoryName: '',
        description: '',
        institutionId: ''
    });
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    const fetchItemCategories = async (page: number) => {
        try {
            setLoading(true);
            const response = await ItemCategoryService.getItemCategoriesByInstitution(page, 10);
            setItemCategories(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load item categories');
            console.error('Error fetching item categories:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItemCategories(0);
    }, []);

    const handleOpenFormModal = (itemCategory?: ItemCategory) => {
        if (itemCategory) {
            setSelectedItemCategory(itemCategory);
            setFormData({
                itemCategoryName: itemCategory.itemCategoryName,
                description: itemCategory.description,
                institutionId: itemCategory.institutionId
            });
        } else {
            setSelectedItemCategory(null);
            setFormData({
                itemCategoryName: '',
                description: '',
                institutionId: ''
            });
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedItemCategory(null);
        setFormData({
            itemCategoryName: '',
            description: '',
            institutionId: ''
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (selectedItemCategory) {
                await ItemCategoryService.updateItemCategory(selectedItemCategory.id!, formData);
            } else {
                formData.institutionId = AuthService.getSelectedInstitution();
                await ItemCategoryService.createItemCategory(formData);
            }
            await fetchItemCategories(pageInfo.currentPage);
            handleCloseFormModal();
        } catch (error) {
            console.error('Error saving item category:', error);
            toast.showApiError(error, 'Failed to save item category');
        }
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Item Category List</h2>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpenFormModal()}
                >
                    Create Item Category
                </Button>
            </div>

            <div style={{ marginBottom: '20px' }}>
                Total Item Categories: {pageInfo.totalElements}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="item-categories-table">
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Category Name</th>
                        <th style={tableHeaderStyle}>Description</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {itemCategories.map(category => (
                        <tr key={category.id} data-testid={`item-category-row-${category.id}`}>
                            <td style={tableCellStyle}>{category.itemCategoryName}</td>
                            <td style={tableCellStyle}>{category.description}</td>
                            <td style={tableCellStyle}>
                                <Tooltip title="Edit Category" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenFormModal(category)}
                                        color="primary"
                                        aria-label="Edit Category"
                                    >
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Form Modal */}
            <Modal
                open={isFormModalOpen}
                onClose={handleCloseFormModal}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1
                }}>
                    <h2>{selectedItemCategory ? 'Edit Item Category' : 'Create Item Category'}</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Category Name"
                            value={formData.itemCategoryName}
                            onChange={(e) => setFormData({ ...formData, itemCategoryName: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            required
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={handleCloseFormModal}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary">
                                {selectedItemCategory ? 'Update' : 'Create'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

        </div>
    );
};

export default ItemCategoryList;
